import React, { useRef, useState, useEffect } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { TimePicker } from "antd"
import type { Dayjs } from "dayjs"
import dayjs from "dayjs"
import * as XLSX from "xlsx"
import { toast } from "react-toastify"
import { useReactToPrint } from "react-to-print"
import TooltipOption from "../../../core/common/tooltipOption"
import CommonSelect from "../../../core/common/commonSelect"
import { all_routes } from "../../router/all_routes"
import {
  createClassRoutine,
  getAllClassRoutines,
  updateClassRoutine,
  deleteClassRoutine,
  getTeacherTimetable,
  type ClassRoutine,
  type ClassRoutineCreateData,
  type ClassRoutineUpdateData,
  type TeacherTimetableResponse,
} from "../../../service/class-routine"
import { getAllClasses, Class } from "../../../service/classes"
import { getAllUsers, USER } from "../../../service/user"
import { getAllCourses, Course } from "../../../service/courses"
import { getAllResultBreakdowns } from "../../../service/result-breakdown"
import { getShiftsByClass, type Shift } from "../../../service/shift"
import { DAY_OPTIONS } from "../../../core/common/selectoption/selectoption"
import { useSelector } from "react-redux"
import { selectSystemsForCurrentBranch } from "../../../core/data/redux/slice/systemsSlice"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos"
import { DEFAULT_BRANCH_ID, DEFAULT_SESSION } from "../../../constant"
import { isTeacher, isStudent } from "../../../utils/rbacHelper"
import { selectUser } from "../../../core/data/redux/slice/user"
import { selectCurrentSession } from "../../../core/data/redux/slice/sessionsSlice"
import { getUserEnrollment } from "../../../service/enrollment"

interface TimetableRow {
  id: string
  course: string
  teacher: string
  startTime: string
  endTime: string
  dayOfWeek: string
  term?: string
  teacherName?: string
}

const ClassTimetable = () => {
  const user = useSelector(selectUser)
  const routes = all_routes
  const [searchParams] = useSearchParams()
  const currentSession = useSelector(selectCurrentSession)
  const classIdFromUrl = searchParams.get("class")
  const componentRef = useRef<HTMLDivElement>(null)
  const systems = useSelector(selectSystemsForCurrentBranch)
  const currentBranch = useSelector(selectCurrentBranch)
  const isUserTeacher = isTeacher(user)
  const isUserStudent = isStudent(user)

  const canViewTimetable =
    user.permissions?.includes("class-timetable.view") ||
    user.permissions?.length === 0
  const canCreateTimetable =
    user.permissions?.includes("class-timetable.create") ||
    user.permissions?.length === 0
  const canEditTimetable =
    user.permissions?.includes("class-timetable.edit") ||
    user.permissions?.length === 0
  const canDeleteTimetable =
    user.permissions?.includes("class-timetable.delete") ||
    user.permissions?.length === 0

  const [data, setData] = useState<ClassRoutine[]>([])
  const [teacherTimetableData, setTeacherTimetableData] = useState<
    TeacherTimetableResponse["data"]
  >([])
  const [allCourses, setAllCourses] = useState<Course[]>([])
  const [studentClassId, setStudentClassId] = useState<string>("")
  const [studentSectionId, setStudentSectionId] = useState<string>("")
  const [studentClassName, setStudentClassName] = useState<string>("My Class")
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10,
  })
  const [filters, setFilters] = useState({
    class: classIdFromUrl || "",
    section: "",
    course: "",
    dayOfWeek: "",
    term: "",
    teacher: "",
  })
  const [appliedFilters, setAppliedFilters] = useState({
    system: systems[0]?._id || "",
  })
  const [classOptions, setClassOptions] = useState<
    { value: string; label: string }[]
  >([])
  const [classes, setClasses] = useState<Class[]>([])
  const [sectionOptions, setSectionOptions] = useState<
    { value: string; label: string }[]
  >([])
  const [teacherOptions, setTeacherOptions] = useState<
    { value: string; label: string }[]
  >([])
  const [filteredSubjectOptions, setFilteredSubjectOptions] = useState<
    { value: string; label: string }[]
  >([])
  const [termOptions, setTermOptions] = useState<
    { value: string; label: string }[]
  >([])
  const [shiftTimings, setShiftTimings] = useState<Shift[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [filtersLoaded, setFiltersLoaded] = useState(false)
  const [hasInitialDataFetched, setHasInitialDataFetched] = useState(false)
  const [activeDay, setActiveDay] = useState("Monday")
  const [dayRows, setDayRows] = useState<{ [key: string]: TimetableRow[] }>({
    Monday: [
      {
        id: "1",
        course: "",
        teacher: "",
        startTime: "",
        endTime: "",
        dayOfWeek: "Monday",
        term: "",
        teacherName: "",
      },
    ],
    Tuesday: [
      {
        id: "1",
        course: "",
        teacher: "",
        startTime: "",
        endTime: "",
        dayOfWeek: "Tuesday",
        term: "",
        teacherName: "",
      },
    ],
    Wednesday: [
      {
        id: "1",
        course: "",
        teacher: "",
        startTime: "",
        endTime: "",
        dayOfWeek: "Wednesday",
        term: "",
        teacherName: "",
      },
    ],
    Thursday: [
      {
        id: "1",
        course: "",
        teacher: "",
        startTime: "",
        endTime: "",
        dayOfWeek: "Thursday",
        term: "",
        teacherName: "",
      },
    ],
    Friday: [
      {
        id: "1",
        course: "",
        teacher: "",
        startTime: "",
        endTime: "",
        dayOfWeek: "Friday",
        term: "",
        teacherName: "",
      },
    ],
    Saturday: [
      {
        id: "1",
        course: "",
        teacher: "",
        startTime: "",
        endTime: "",
        dayOfWeek: "Saturday",
        term: "",
        teacherName: "",
      },
    ],
    Sunday: [
      {
        id: "1",
        course: "",
        teacher: "",
        startTime: "",
        endTime: "",
        dayOfWeek: "Sunday",
        term: "",
        teacherName: "",
      },
    ],
  })

  const getInitialFormData = (): ClassRoutineCreateData => ({
    teacher: "",
    branch: DEFAULT_BRANCH_ID as string,
    session: currentSession?._id || (DEFAULT_SESSION as string),
    class: "",
    section: "",
    course: "",
    dayOfWeek: "Monday",
    startTime: "",
    endTime: "",
    isActive: true,
    system: appliedFilters.system || systems[0]?._id || "",
    term: "",
  })

  const [formData, setFormData] =
    useState<ClassRoutineCreateData>(getInitialFormData())
  const [editFormData, setEditFormData] = useState<ClassRoutineUpdateData>({})
  const [selectedRecord, setSelectedRecord] = useState<ClassRoutine | null>(
    null,
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null)

  // Print configuration for A4 Landscape with 7-day support
  // Print configuration for A4 Landscape with proper 7-day support
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    pageStyle: `
    @media print {
      @page {
        size: A4 landscape;
        margin: 8mm 5mm 8mm 5mm;
      }
      
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      
      body {
        margin: 0;
        padding: 0;
        background: white;
      }
      
      /* Hide all non-printable elements */
      .page-wrapper > .content > .card:first-of-type:not(.printable-content),
      .card-header,
      .dropdown,
      .action-drop,
      [data-bs-toggle="dropdown"],
      .btn,
      .tooltip-option,
      .right-content,
      .modal,
      .modal-backdrop,
      .dropdown-menu,
      .filter-dropdown,
      .d-md-flex .my-auto.mb-2 + .d-flex,
      .card.mb-4:first-of-type {
        display: none !important;
      }
      
      /* Printable content container */
      .printable-content {
        margin-top: 0 !important;
        padding-top: 10px !important;
        break-inside: avoid;
        page-break-inside: avoid;
        box-shadow: none !important;
        border: none !important;
        width: 100% !important;
        max-width: 100% !important;
      }
      
      .printable-content .card-body {
        padding: 10px 5px 5px 5px !important;
        overflow-x: visible !important;
      }
      
      /* Timetable layout - adjust for 7 columns */
      .d-flex.flex-nowrap {
        display: flex !important;
        flex-wrap: nowrap !important;
        flex-direction: row !important;
        justify-content: space-between !important;
        gap: 4px !important;
        width: 100% !important;
      }
      
      .d-flex.flex-column {
        break-inside: avoid;
        page-break-inside: avoid;
        flex: 1 !important;
        min-width: 115px !important;
        max-width: 135px !important;
        width: auto !important;
      }
      
      /* Day header styling */
      .mb-3 h6 {
        font-size: 11px !important;
        font-weight: bold !important;
        color: #333 !important;
        margin-bottom: 6px !important;
        padding-bottom: 4px !important;
        border-bottom: 2px solid #ddd !important;
        text-align: center !important;
        white-space: nowrap !important;
      }
      
      /* Card styling for each timetable entry */
      .bg-transparent-danger,
      .bg-transparent-primary,
      .bg-transparent-success,
      .bg-transparent-pending,
      .bg-transparent-info,
      .bg-transparent-light,
      .bg-transparent-warning {
        break-inside: avoid;
        page-break-inside: avoid;
        border: 1px solid #e0e0e0 !important;
        margin-bottom: 6px !important;
        padding: 4px 6px !important;
        border-radius: 4px !important;
        background-color: #f9f9f9 !important;
      }
      
      /* Time and course styling */
      .small {
        font-size: 8px !important;
        line-height: 1.2 !important;
      }
      
      .fw-semibold {
        font-weight: 600 !important;
        font-size: 9px !important;
      }
      
      .text-dark {
        color: #000 !important;
      }
      
      .text-muted {
        color: #666 !important;
        font-size: 8px !important;
      }
      
      /* Icon styling - hide icons in print */
      .ti,
      .ti-clock,
      .ti-calendar-off {
        display: none !important;
      }
      
      /* Empty state styling */
      .text-center.text-muted.p-3 {
        padding: 8px 4px !important;
        font-size: 8px !important;
        color: #999 !important;
        text-align: center !important;
        border: 1px dashed #ddd !important;
        border-radius: 4px !important;
      }
      
      /* Spacing utilities */
      .me-4 {
        margin-right: 4px !important;
      }
      
      .mb-2 {
        margin-bottom: 6px !important;
      }
      
      .p-2 {
        padding: 4px !important;
      }
      
      .pe-4 {
        padding-right: 8px !important;
      }
      
      .mt-2 {
        margin-top: 4px !important;
      }
      
      /* Title/header for printed page */
      .print-header {
        display: block !important;
        text-align: center;
        margin-bottom: 10px;
        padding-bottom: 6px;
        border-bottom: 2px solid #333;
      }
      
      .print-header h3 {
        font-size: 14px;
        margin: 0;
        color: #000;
      }
      
      .print-header p {
        font-size: 9px;
        margin: 3px 0 0;
        color: #666;
      }
      
      /* Ensure content doesn't get cut off */
      .card-body.pb-0 {
        padding-bottom: 0 !important;
      }
      
      .h-100 {
        height: auto !important;
      }
      
      /* Fix for any overflow issues */
      .overflow-auto,
      .overflow-y-auto {
        overflow: visible !important;
      }
      
      /* Ensure all columns are visible */
      .d-flex.flex-column:last-child {
        margin-right: 0 !important;
      }
      
      /* Force all days to be visible */
      .d-flex.flex-nowrap > .d-flex.flex-column {
        display: flex !important;
        visibility: visible !important;
      }
    }
  `,
  })

  const fetchStudentEnrollment = async () => {
    if (isUserStudent && user._id && currentSession?._id) {
      try {
        const enrollmentResponse = await getUserEnrollment(
          user._id,
          currentSession?._id,
        )
        if (enrollmentResponse.success && enrollmentResponse.data) {
          const enrollmentData = enrollmentResponse.data
          const classId = enrollmentData.class?._id || ""
          const sectionId = enrollmentData.section?._id || ""
          const className = enrollmentData.class?.name || "My Class"
          if (classId) {
            setStudentClassId(classId)
            setStudentSectionId(sectionId)
            setStudentClassName(className)
            setFilters(prev => ({
              ...prev,
              class: classId,
              section: sectionId,
            }))
            if (appliedFilters.system) {
              await fetchShiftTimings(classId, appliedFilters.system)
            }
            setFiltersLoaded(true)
          } else {
            toast.warning("No class enrollment found for current session")
            setFiltersLoaded(true)
          }
        } else {
          toast.warning("Unable to load student enrollment details")
          setFiltersLoaded(true)
        }
      } catch (error) {
        console.error("Error fetching student enrollment:", error)
        toast.error("Failed to load student details")
        setFiltersLoaded(true)
      }
    }
  }

  useEffect(() => {
    const loadInitialData = async () => {
      if (!isInitialLoad) return
      setIsLoading(true)
      try {
        await fetchTeachers()
        if (isUserStudent) {
          await fetchStudentEnrollment()
        } else if (isUserTeacher) {
          setFiltersLoaded(true)
          setIsInitialLoad(false)
        } else {
          if (systems.length > 0) {
            const systemId = systems[0]._id
            setAppliedFilters(prev => ({ ...prev, system: systemId }))
            await fetchClassesBySystem(systemId)
          } else {
            setFiltersLoaded(true)
          }
        }
      } catch (error) {
        console.error("Initial load error:", error)
        toast.error("Failed to load initial data")
        setFiltersLoaded(true)
      } finally {
        setIsLoading(false)
      }
    }
    loadInitialData()
  }, [isInitialLoad])

  useEffect(() => {
    const fetchInitialTimetableData = async () => {
      if (!filtersLoaded || hasInitialDataFetched || isLoading) return
      try {
        setIsLoading(true)
        if (isUserTeacher) {
          await fetchTeacherTimetable()
        } else {
          await fetchClassRoutines()
        }
        setHasInitialDataFetched(true)
        setIsInitialLoad(false)
      } catch (error) {
        console.error("Error fetching initial timetable:", error)
        setHasInitialDataFetched(true)
        setIsInitialLoad(false)
      } finally {
        setIsLoading(false)
      }
    }
    fetchInitialTimetableData()
  }, [filtersLoaded, hasInitialDataFetched, isUserTeacher])

  useEffect(() => {
    if (!hasInitialDataFetched || isLoading) return
    const timer = setTimeout(() => {
      if (isUserTeacher) {
        fetchTeacherTimetable()
      } else {
        fetchClassRoutines()
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [
    filters.class,
    filters.section,
    filters.course,
    filters.dayOfWeek,
    filters.term,
    appliedFilters.system,
    isUserTeacher,
    hasInitialDataFetched,
  ])

  if (isUserTeacher && !canViewTimetable) {
    return (
      <div className="page-wrapper">
        <div className="content content-two">
          <div className="card">
            <div className="card-body text-center py-5">
              <i
                className="ti ti-lock-off text-warning mb-3"
                style={{ fontSize: "3rem" }}
              ></i>
              <h4 className="text-warning mb-2">Access Denied</h4>
              <p className="text-muted mb-0">
                You don't have permission to view the timetable. Please contact
                administrator.
              </p>
              <Link to={routes.adminDashboard} className="btn btn-primary mt-3">
                <i className="ti ti-arrow-left me-2" />
                Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const generateRowId = () =>
    `row_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

  const addDayRow = (day: string) => {
    const currentDayRows = dayRows[day]
    const lastRow = currentDayRows[currentDayRows.length - 1]
    if (!lastRow.course || !lastRow.startTime || !lastRow.endTime) {
      toast.warning(
        `Please complete the current row for ${day} before adding a new one`,
      )
      return
    }
    const newRow: TimetableRow = {
      id: generateRowId(),
      course: "",
      teacher: "",
      startTime: "",
      endTime: "",
      dayOfWeek: day,
      term: formData.term || "",
      teacherName: "",
    }
    setDayRows(prev => ({
      ...prev,
      [day]: [...prev[day], newRow],
    }))
  }

  const removeDayRow = (day: string, rowId: string) => {
    if (dayRows[day].length === 1) {
      toast.warning("At least one row must remain")
      return
    }
    setDayRows(prev => ({
      ...prev,
      [day]: prev[day].filter(row => row.id !== rowId),
    }))
  }

  const updateDayRow = (
    day: string,
    rowId: string,
    field: keyof TimetableRow,
    value: string,
  ) => {
    setDayRows(prev => ({
      ...prev,
      [day]: prev[day].map(row =>
        row.id === rowId ? { ...row, [field]: value } : row,
      ),
    }))
  }

  const fetchTeachers = async () => {
    try {
      const usersResponse = await getAllUsers({
        status: "active",
      })
      const teacherOptionsData = usersResponse.data
        .filter((user: USER) => {
          const isTeacher =
            typeof user.role === "object" &&
            user.role !== null &&
            "name" in user.role &&
            user.role.name.toLowerCase() === "teacher"
          return isTeacher
        })
        .map((user: USER) => ({
          value: user._id,
          label: `${user.firstName} ${user.lastName}`,
          userObject: user,
        }))
      setTeacherOptions(teacherOptionsData)
    } catch (error) {
      console.error("Error fetching teachers:", error)
    }
  }

  const handleCourseSelection = async (
    day: string,
    rowId: string,
    courseId: string,
  ) => {
    updateDayRow(day, rowId, "course", courseId)
    if (!courseId) {
      updateDayRow(day, rowId, "teacher", "")
      updateDayRow(day, rowId, "teacherName", "")
      return
    }
    try {
      const coursesResponse = await getAllCourses({})
      if (coursesResponse.data && coursesResponse.data.length > 0) {
        updateDayRow(day, rowId, "teacher", "")
        updateDayRow(day, rowId, "teacherName", "")
      } else {
        updateDayRow(day, rowId, "teacher", "")
        updateDayRow(day, rowId, "teacherName", "")
      }
    } catch (error) {
      console.error("Error fetching course details:", error)
      updateDayRow(day, rowId, "teacher", "")
      updateDayRow(day, rowId, "teacherName", "")
    }
  }

  const fetchClassesBySystem = async (systemId: string) => {
    try {
      // Without branch, a Class shared across campuses returns every
      // campus's same-named Section mixed into one list (e.g. "A" 2-3x,
      // indistinguishable) -- same fix as Marks Tabulation/Report Cards.
      // Session too -- Class.sections accumulates every session's sections
      // over the class's lifetime (never pruned), so last year's "A" and
      // this year's "A" both show unless session narrows it down as well.
      const classesResponse = await getAllClasses({
        system: systemId,
        branch: currentBranch?._id,
        session: currentSession?._id,
      })
      const classOptionsData = classesResponse.classes.map((cls: Class) => ({
        value: cls._id,
        label: cls.name,
      }))
      setClassOptions(classOptionsData)
      setClasses(classesResponse.classes)
      const defaultClassId = classIdFromUrl || classOptionsData[0]?.value || ""
      setFilters(prev => ({
        ...prev,
        class: defaultClassId,
      }))
      setFormData(prev => ({
        ...prev,
        class: defaultClassId,
        system: systemId,
      }))
      if (defaultClassId && !isUserTeacher) {
        await Promise.all([
          filterCoursesByClass(defaultClassId),
          fetchTermsByClassAndSession(defaultClassId),
          fetchSectionsByClass(defaultClassId),
          fetchShiftTimings(defaultClassId, systemId),
        ])
      }
      if (!isUserTeacher) {
        await fetchDropdownData()
      }
      setFiltersLoaded(true)
      setIsInitialLoad(false)
    } catch (error) {
      console.error("Error fetching classes by system:", error)
      setClassOptions([])
      setFiltersLoaded(true)
      setIsInitialLoad(false)
    }
  }

  const fetchSectionsByClass = async (classId: string) => {
    try {
      if (!classId) {
        setSectionOptions([])
        return
      }
      const classesResponse = await getAllClasses({
        system: appliedFilters.system,
        branch: currentBranch?._id,
        session: currentSession?._id,
      })
      const selectedClass = classesResponse.classes.find(
        (cls: Class) => cls._id === classId,
      )
      const classSections = selectedClass?.sections || []
      const sectionOptionsData = classSections.map((section: any) => ({
        value: section._id,
        label: section.name,
      }))
      setSectionOptions(sectionOptionsData)
      if (sectionOptionsData.length > 0) {
        const firstSection = sectionOptionsData[0]
        setFilters(prev => ({
          ...prev,
          section: firstSection.value,
        }))
        setFormData(prev => ({
          ...prev,
          section: firstSection.value,
        }))
      }
    } catch (error) {
      console.error("Error fetching sections by class:", error)
      setSectionOptions([])
    }
  }

  const fetchShiftTimings = async (classId: string, systemId: string) => {
    if (!classId || !systemId) {
      setShiftTimings([])
      return
    }
    try {
      const shiftsResponse = await getShiftsByClass(
        classId,
        systemId,
        DEFAULT_BRANCH_ID as string,
      )
      const responseData = (shiftsResponse as any).data
      const shiftsData = responseData?.shifts || []
      setShiftTimings(shiftsData)
    } catch (error) {
      console.error("Error fetching shift timings:", error)
      setShiftTimings([])
    }
  }

  const fetchTermsByClassAndSession = async (classId: string) => {
    if (!classId || !currentSession?._id) {
      setTermOptions([])
      return
    }
    try {
      // status: "active" excluded every real breakdown -- see Marks Entry/
      // Marks Tabulation/Report Cards for the same fix and why.
      const resultBreakdownsResponse = await getAllResultBreakdowns({
        classId: classId,
        sessionId: currentSession?._id,
        all: true,
        limit: 1000,
        page: 1,
      })
      const breakdowns = resultBreakdownsResponse?.data || []
      const termOptionsData = breakdowns.map((breakdown: any) => ({
        value: breakdown._id,
        label: breakdown.title,
      }))
      setTermOptions(termOptionsData)
      if (termOptionsData.length > 0) {
        setFormData(prev => ({
          ...prev,
          term: termOptionsData[0].value,
        }))
        setFilters(prev => ({
          ...prev,
          term: termOptionsData[0].value,
        }))
      } else {
        setFormData(prev => ({
          ...prev,
          term: "",
        }))
        setFilters(prev => ({
          ...prev,
          term: "",
        }))
      }
    } catch (error) {
      console.error("Error fetching terms by class:", error)
      setTermOptions([])
    }
  }

  const filterCoursesByClass = async (_classId: string) => {
    try {
      const coursesResponse = await getAllCourses({})
      const subjectOptionsData = (coursesResponse.data || []).map(
        (course: Course) => ({
          value: course._id,
          label: course.title || course.courseIdentifier,
        }),
      )
      setFilteredSubjectOptions(subjectOptionsData)
    } catch (error) {
      console.error("Error loading courses for timetable:", error)
      setFilteredSubjectOptions([])
    }
  }

  const filterCoursesForEdit = async (_classId: string) => {
    try {
      const coursesResponse = await getAllCourses({})
      const subjectOptionsData = (coursesResponse.data || []).map(
        (course: Course) => ({
          value: course._id,
          label: course.title || course.courseIdentifier,
        }),
      )
      setFilteredSubjectOptions(subjectOptionsData)
    } catch (error) {
      console.error("Error loading courses for edit:", error)
      setFilteredSubjectOptions([])
    }
  }

  const fetchDropdownData = async () => {
    try {
      if (!isUserStudent && !isUserTeacher) {
        const coursesResponse = await getAllCourses({})
        const subjectOptionsData = coursesResponse.data.map(
          (course: Course) => ({
            value: course._id,
            label: course.title || course.courseIdentifier,
          }),
        )
        setFilteredSubjectOptions(subjectOptionsData)
      } else if (isUserTeacher) {
        const coursesResponse = await getAllCourses({})
        const subjectOptionsData = coursesResponse.data.map(
          (course: Course) => ({
            value: course._id,
            label: course.title || course.courseIdentifier,
          }),
        )
        setFilteredSubjectOptions(subjectOptionsData)
      }
    } catch (error) {
      console.error("Error fetching dropdown data:", error)
      toast.error("Failed to load dropdown data")
    }
  }

  const fetchClassRoutines = async () => {
    if (isLoading) return
    try {
      setIsLoading(true)
      const params: any = {
        page: pagination.currentPage,
        limit: pagination.limit,
        session: currentSession?._id,
      }
      if (isUserStudent) {
        if (studentClassId) {
          params.class = studentClassId
          params.section = studentSectionId
        } else {
          setData([])
          setPagination({
            currentPage: 1,
            totalPages: 1,
            total: 0,
            limit: 10,
          })
          setIsLoading(false)
          return
        }
      } else {
        if (filters.class) params.class = filters.class
        if (filters.section) params.section = filters.section
        if (filters.course) params.course = filters.course
        if (filters.dayOfWeek) params.dayOfWeek = filters.dayOfWeek
        if (filters.term) params.term = filters.term
        if (filters.teacher) params.teacher = filters.teacher
        if (appliedFilters.system) params.system = appliedFilters.system
      }
      const response = await getAllClassRoutines(params)
      if (response?.data) {
        try {
          const coursesResponse = await getAllCourses({})
          setAllCourses(coursesResponse.data || [])
          const routinesWithCourses = response.data.map(
            (routine: ClassRoutine) => {
              if (routine.course) {
                const courseId =
                  typeof routine.course === "string"
                    ? routine.course
                    : routine.course._id || routine.course
                const courseData = coursesResponse.data.find(
                  (course: Course) => course._id === courseId,
                )
                if (courseData) {
                  return {
                    ...routine,
                    course: {
                      _id: courseData._id,
                      title: courseData.title,
                      courseIdentifier: courseData.courseIdentifier,
                      name: courseData.title || courseData.courseIdentifier,
                    },
                  }
                }
              }
              return routine
            },
          )
          setData(routinesWithCourses)
        } catch (error) {
          console.error("Error fetching courses:", error)
          setData(response.data)
        }
        setPagination(
          response.pagination || {
            currentPage: 1,
            totalPages: 1,
            total: 0,
            limit: 10,
          },
        )
      } else {
        setData([])
      }
    } catch (error: any) {
      console.error("Error fetching class routines:", error)
      toast.error(
        error.response?.data?.message || "Failed to fetch class routines",
      )
      setData([])
    } finally {
      setIsLoading(false)
    }
  }

  const fetchTeacherTimetable = async () => {
    if (isLoading || !isUserTeacher || !user?._id) return
    try {
      setIsLoading(true)
      const response = await getTeacherTimetable(user._id, {
        termId: currentSession?._id || (DEFAULT_SESSION as string),
        classId: filters.class || undefined,
      })
      if (response?.data) {
        try {
          const coursesResponse = await getAllCourses({})
          setAllCourses(coursesResponse.data || [])
          const timetableWithCourses = response.data.map(
            (routine: ClassRoutine) => {
              if (routine.course) {
                const courseId =
                  typeof routine.course === "string"
                    ? routine.course
                    : routine.course._id || routine.course
                const courseData = coursesResponse.data.find(
                  (course: Course) => course._id === courseId,
                )
                if (courseData) {
                  return {
                    ...routine,
                    course: {
                      _id: courseData._id,
                      title: courseData.title,
                      courseIdentifier: courseData.courseIdentifier,
                      name: courseData.title || courseData.courseIdentifier,
                    },
                  }
                }
              }
              return routine
            },
          )
          setTeacherTimetableData(timetableWithCourses)
        } catch (error) {
          console.error("Error fetching courses:", error)
          setTeacherTimetableData(response.data)
        }
      } else {
        setTeacherTimetableData([])
      }
    } catch (error: any) {
      console.error("Error fetching teacher timetable:", error)
      toast.error(
        error.response?.data?.message || "Failed to fetch teacher timetable",
      )
      setTeacherTimetableData([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleRefresh = async () => {
    if (isLoading) return
    setIsLoading(true)
    try {
      if (isUserTeacher) {
        await fetchTeacherTimetable()
        toast.success("Your timetable refreshed successfully")
      } else {
        await fetchClassRoutines()
        toast.success("Time table refreshed successfully")
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleSystemChange = async (systemItem: any) => {
    const systemId = systemItem?._id || ""
    setAppliedFilters(prev => ({
      ...prev,
      system: systemId,
    }))
    setFormData(prev => ({
      ...prev,
      system: systemId,
    }))
    setIsLoading(true)
    try {
      await fetchClassesBySystem(systemId)
      setHasInitialDataFetched(false)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClassFilterChange = (selectedOption: any) => {
    const classId = selectedOption?.value || ""
    if (filters.class === classId) return
    setFilters(prev => ({
      ...prev,
      class: classId,
      section: "",
    }))
    setPagination(prev => ({ ...prev, currentPage: 1 }))
    if (classId && !isUserTeacher) {
      setIsLoading(true)
      Promise.all([
        filterCoursesByClass(classId),
        fetchTermsByClassAndSession(classId),
        fetchSectionsByClass(classId),
        appliedFilters.system
          ? fetchShiftTimings(classId, appliedFilters.system)
          : Promise.resolve(),
      ]).finally(() => {
        setIsLoading(false)
      })
    } else {
      setSectionOptions([])
    }
  }

  const handleSectionFilterChange = (selectedOption: any) => {
    const sectionId = selectedOption?.value || ""
    if (filters.section === sectionId) return
    setFilters(prev => ({
      ...prev,
      section: sectionId,
    }))
    setPagination(prev => ({ ...prev, currentPage: 1 }))
  }

  const formatTimeForBackend = (timeStr: string) => {
    if (!timeStr) return ""
    if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timeStr)) {
      return timeStr
    }
    try {
      return dayjs(timeStr, "h:mm A").format("HH:mm")
    } catch (error) {
      console.error("Error formatting time:", error)
      return timeStr
    }
  }

  const formatTimeForDisplay = (timeStr: string) => {
    if (!timeStr) return null
    try {
      if (timeStr.includes(":")) {
        return dayjs(`2000-01-01 ${timeStr}`)
      }
      return dayjs(timeStr, "h:mm A")
    } catch (error) {
      console.error("Error parsing time:", error, timeStr)
      return null
    }
  }

  const handleCreateClassRoutine = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setIsSubmitting(true)
      if (
        !formData.class ||
        !formData.section ||
        !formData.system ||
        !formData.term
      ) {
        toast.warning("Please select Class, Section and Term")
        return
      }
      const activeDayRows = dayRows[activeDay].filter(
        row => row.course && row.teacher && row.startTime && row.endTime,
      )
      if (activeDayRows.length === 0) {
        toast.warning(
          `Please add at least one valid timetable entry for ${activeDay} with Course, Teacher, Start Time and End Time`,
        )
        return
      }
      const createPromises = activeDayRows.map(async row => {
        const timetableData: ClassRoutineCreateData = {
          teacher: row.teacher,
          branch: DEFAULT_BRANCH_ID as string,
          session: currentSession?._id || (DEFAULT_SESSION as string),
          class: formData.class,
          section: formData.section,
          course: row.course,
          dayOfWeek: row.dayOfWeek,
          startTime: formatTimeForBackend(row.startTime),
          endTime: formatTimeForBackend(row.endTime),
          isActive: formData.isActive,
          system: formData.system,
          term: formData.term,
        }
        return createClassRoutine(timetableData)
      })
      await Promise.all(createPromises)
      toast.success("Timetable created successfully")
      setFormData(getInitialFormData())
      setActiveDay("Monday")
      setDayRows({
        Monday: [
          {
            id: "1",
            course: "",
            teacher: "",
            startTime: "",
            endTime: "",
            dayOfWeek: "Monday",
            term: "",
            teacherName: "",
          },
        ],
        Tuesday: [
          {
            id: "1",
            course: "",
            teacher: "",
            startTime: "",
            endTime: "",
            dayOfWeek: "Tuesday",
            term: "",
            teacherName: "",
          },
        ],
        Wednesday: [
          {
            id: "1",
            course: "",
            teacher: "",
            startTime: "",
            endTime: "",
            dayOfWeek: "Wednesday",
            term: "",
            teacherName: "",
          },
        ],
        Thursday: [
          {
            id: "1",
            course: "",
            teacher: "",
            startTime: "",
            endTime: "",
            dayOfWeek: "Thursday",
            term: "",
            teacherName: "",
          },
        ],
        Friday: [
          {
            id: "1",
            course: "",
            teacher: "",
            startTime: "",
            endTime: "",
            dayOfWeek: "Friday",
            term: "",
            teacherName: "",
          },
        ],
        Saturday: [
          {
            id: "1",
            course: "",
            teacher: "",
            startTime: "",
            endTime: "",
            dayOfWeek: "Saturday",
            term: "",
            teacherName: "",
          },
        ],
        Sunday: [
          {
            id: "1",
            course: "",
            teacher: "",
            startTime: "",
            endTime: "",
            dayOfWeek: "Sunday",
            term: "",
            teacherName: "",
          },
        ],
      })
      const modal = document.getElementById("add_time_table")
      if (modal) {
        const bootstrapModal = (window as any).bootstrap?.Modal?.getInstance(
          modal,
        )
        if (bootstrapModal) {
          bootstrapModal.hide()
        } else {
          const closeButton = modal.querySelector(
            '[data-bs-dismiss="modal"]',
          ) as HTMLElement
          if (closeButton) closeButton.click()
        }
      }
      if (isUserTeacher) {
        fetchTeacherTimetable()
      } else {
        fetchClassRoutines()
      }
    } catch (error: any) {
      console.error("Error creating timetable:", error)
      toast.error(error.response?.data?.message || "Failed to create timetable")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUpdateClassRoutine = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedRecord) {
      toast.warning("No time table selected for update")
      return
    }
    try {
      setIsSubmitting(true)
      const updateData = { ...editFormData }
      if (updateData.startTime) {
        updateData.startTime = formatTimeForBackend(updateData.startTime)
      }
      if (updateData.endTime) {
        updateData.endTime = formatTimeForBackend(updateData.endTime)
      }
      await updateClassRoutine(selectedRecord._id, updateData)
      toast.success("Time table updated successfully")
      setEditFormData({})
      setSelectedRecord(null)
      const modal = document.getElementById("edit_time_table")
      if (modal) {
        const bootstrapModal = (window as any).bootstrap?.Modal?.getInstance(
          modal,
        )
        if (bootstrapModal) {
          bootstrapModal.hide()
        } else {
          const closeButton = modal.querySelector(
            '[data-bs-dismiss="modal"]',
          ) as HTMLElement
          if (closeButton) closeButton.click()
        }
      }
      if (isUserTeacher) {
        fetchTeacherTimetable()
      } else {
        fetchClassRoutines()
      }
    } catch (error: any) {
      console.error("Error updating time table:", error)
      toast.error(
        error.response?.data?.message || "Failed to update time table",
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteClassRoutine = async () => {
    if (!selectedRecord) {
      toast.warning("No time table selected for deletion")
      return
    }
    try {
      await deleteClassRoutine(selectedRecord._id)
      toast.success("Time table deleted successfully")
      const modal = document.getElementById("delete-modal")
      if (modal) {
        const bootstrapModal = (window as any).bootstrap?.Modal?.getInstance(
          modal,
        )
        if (bootstrapModal) {
          bootstrapModal.hide()
        } else {
          const closeButton = modal.querySelector(
            '[data-bs-dismiss="modal"]',
          ) as HTMLElement
          if (closeButton) closeButton.click()
        }
      }
      if (isUserTeacher) {
        fetchTeacherTimetable()
      } else {
        fetchClassRoutines()
      }
    } catch (error: any) {
      console.error("Error deleting time table:", error)
      toast.error(
        error.response?.data?.message || "Failed to delete time table",
      )
    }
  }

  const handleEditClick = async (record: ClassRoutine) => {
    try {
      setSelectedRecord(record)
      if (record.class?._id && !isUserStudent && !isUserTeacher) {
        await filterCoursesForEdit(record.class._id)
        await fetchTermsByClassAndSession(record.class._id)
        await fetchSectionsByClass(record.class._id)
      }
      const teacherName = record.teacher
        ? `${record.teacher.firstName || ""} ${
            record.teacher.lastName || ""
          }`.trim()
        : ""
      setEditFormData({
        teacher: record.teacher?._id || "",
        teacherName: teacherName,
        class: record.class?._id || "",
        section: record.section?._id || "",
        course: record.course?._id || "",
        dayOfWeek: record.dayOfWeek,
        startTime: record.startTime,
        endTime: record.endTime,
        isActive: record.isActive,
        term: record.term || "",
      })
      setActiveDay(record.dayOfWeek)
    } catch (error: any) {
      console.error("Error loading time table data:", error)
      toast.error("Failed to load time table data")
    }
  }

  const handleFilterChange = (key: string, value: string) => {
    if (filters[key as keyof typeof filters] === value) return
    setFilters(prev => ({ ...prev, [key]: value }))
    setPagination(prev => ({ ...prev, currentPage: 1 }))
    setIsLoading(true)
    setTimeout(() => setIsLoading(false), 500)
  }

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))
    if (field === "class" && !isUserTeacher && !isUserStudent) {
      setIsLoading(true)
      Promise.all([
        filterCoursesByClass(value),
        fetchTermsByClassAndSession(value),
        fetchSectionsByClass(value),
        appliedFilters.system
          ? fetchShiftTimings(value, appliedFilters.system)
          : Promise.resolve(),
      ]).finally(() => setIsLoading(false))
      setFormData(prev => ({
        ...prev,
        class: value,
        section: "",
      }))
    }
    if (field === "dayOfWeek") {
      setActiveDay(value)
    }
    if (field === "term") {
      setDayRows(prev => {
        const updatedDayRows = { ...prev }
        Object.keys(updatedDayRows).forEach(day => {
          updatedDayRows[day] = updatedDayRows[day].map(row => ({
            ...row,
            term: value,
          }))
        })
        return updatedDayRows
      })
    }
  }

  const handleEditInputChange = (field: string, value: any) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value,
    }))
    if (field === "class" && !isUserStudent && !isUserTeacher) {
      setIsLoading(true)
      Promise.all([
        filterCoursesForEdit(value),
        fetchTermsByClassAndSession(value),
        fetchSectionsByClass(value),
      ]).finally(() => setIsLoading(false))
      setEditFormData(prev => ({
        ...prev,
        class: value,
        course: "",
        section: "",
      }))
    }
  }

  const handleTimeChange = (
    day: string,
    rowId: string,
    field: "startTime" | "endTime",
    time: Dayjs | null,
  ) => {
    if (time) {
      updateDayRow(day, rowId, field, time.format("h:mm A"))
    }
  }

  const handleEditTimeChange = (
    field: "startTime" | "endTime",
    time: Dayjs | null,
  ) => {
    if (time) {
      setEditFormData(prev => ({
        ...prev,
        [field]: time.format("h:mm A"),
      }))
    }
  }

  const handleResetFilters = () => {
    setFilters({
      class: classIdFromUrl || "",
      section: "",
      course: "",
      dayOfWeek: "",
      term: "",
      teacher: "",
    })
  }

  const handleApplyClick = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show")
    }
    setIsLoading(true)
    setTimeout(() => {
      if (isUserTeacher) {
        fetchTeacherTimetable()
      } else {
        fetchClassRoutines()
      }
    }, 500)
  }

  const getModalContainer = () => document.body

  const handleExportExcel = () => {
    const exportData = isUserTeacher ? teacherTimetableData : data
    const ws = XLSX.utils.json_to_sheet(
      exportData.map(routine => ({
        Class: routine.class?.name || "N/A",
        Section: routine.section?.name || "N/A",
        Teacher:
          routine.teacher?.firstName && routine.teacher?.lastName
            ? `${routine.teacher.firstName} ${routine.teacher.lastName}`
            : routine.teacher?.email || "N/A",
        Course:
          routine.course?.title ||
          routine.course?.courseIdentifier ||
          routine.course?.name ||
          "N/A",
        Day: routine.dayOfWeek || "N/A",
        "Start Time": routine.startTime || "N/A",
        "End Time": routine.endTime || "N/A",
        Term: termOptions.find(t => t.value === routine.term)?.label || "N/A",
        Status: routine.isActive ? "Active" : "Inactive",
        "Created Date": new Date(routine.createdAt).toLocaleDateString(),
      })),
    )
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      isUserTeacher ? "My Timetable" : "Time Table",
    )
    XLSX.writeFile(wb, isUserTeacher ? "my_timetable.xlsx" : "time_table.xlsx")
  }

  const getDisplayData = () => {
    if (isUserTeacher) {
      return teacherTimetableData
    }
    return data
  }

  const groupByDay = (routines: any[]) => {
    const days = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ]
    const grouped: { [key: string]: any[] } = {}
    days.forEach(day => {
      grouped[day] = routines.filter(routine => routine.dayOfWeek === day)
    })
    return grouped
  }

  const displayData = getDisplayData()
  const groupedData = groupByDay(displayData)

  const getColorClass = (index: number) => {
    const colors = [
      "transparent-danger",
      "transparent-primary",
      "transparent-success",
      "transparent-pending",
      "transparent-info",
      "transparent-light",
      "transparent-warning",
    ]
    return colors[index % colors.length]
  }

  const selectedClassName = isUserStudent
    ? studentClassName
    : classOptions.find(cls => cls.value === (filters.class || classIdFromUrl))
        ?.label || "Selected Class"

  const getTeacherName = (teacherId: string) => {
    const teacher = teacherOptions.find(t => t.value === teacherId)
    return teacher ? teacher.label : ""
  }

  const showActions = !isUserStudent && (canEditTimetable || canDeleteTimetable)
  const shouldShowTimetable = hasInitialDataFetched && data.length > 0

  return (
    <div>
      <div className="page-wrapper">
        <div className="content content-two">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">
                {isUserTeacher
                  ? "My Timetable"
                  : isUserStudent
                    ? `${selectedClassName} - My Timetable`
                    : classIdFromUrl || filters.class
                      ? `${selectedClassName} - Time Table`
                      : "Time Table"}
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Academic </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isUserTeacher
                      ? "My Timetable"
                      : isUserStudent
                        ? "My Timetable"
                        : classIdFromUrl || filters.class
                          ? `${selectedClassName} Time Table`
                          : "All Time Tables"}
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={handleRefresh}
                onExportPdf={handlePrint}
                onExportExcel={handleExportExcel}
              />
              {canCreateTimetable && !isUserTeacher && !isUserStudent && (
                <div className="mb-2">
                  <Link
                    to="#"
                    className={`btn btn-primary ${
                      user.permissions?.includes("class-timetable.create") ||
                      !user.permissions?.length
                        ? ""
                        : "not-allowed"
                    }`}
                    data-bs-toggle="modal"
                    data-bs-target="#add_time_table"
                    onClick={e => {
                      if (
                        !user.permissions?.includes("class-timetable.create") &&
                        !!user.permissions?.length
                      ) {
                        e.preventDefault()
                        e.stopPropagation()
                      } else {
                        setFormData(getInitialFormData())
                        setActiveDay("Monday")
                      }
                    }}
                  >
                    <i className="ti ti-square-rounded-plus-filled me-2" />
                    Add Time Table
                  </Link>
                </div>
              )}
            </div>
          </div>
          <div className="card mb-4">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">
                {isUserTeacher
                  ? "My Teaching Schedule"
                  : isUserStudent
                    ? `${selectedClassName} - My Timetable`
                    : classIdFromUrl || filters.class
                      ? `${selectedClassName} - Time Table`
                      : "All Time Tables"}
              </h4>
              <div className="d-flex align-items-center flex-wrap">
                {!isUserStudent && (
                  <>
                    {!isUserTeacher && (
                      <div className="mb-3 me-2">
                        <CommonSelect
                          className="select"
                          options={systems}
                          value={systems.find(
                            (item: any) => item._id === appliedFilters.system,
                          )}
                          getOptionValue={item => item._id}
                          getOptionLabel={item => item.name}
                          onChange={handleSystemChange}
                        />
                      </div>
                    )}
                    <div className="d-flex align-items-center flex-wrap mb-3">
                      {!isUserTeacher && (
                        <div className="me-2">
                          <CommonSelect
                            className="select"
                            options={classOptions}
                            value={
                              classOptions.find(
                                option => option.value === filters.class,
                              ) || null
                            }
                            onChange={handleClassFilterChange}
                            placeholder="Select Class"
                          />
                        </div>
                      )}
                      {!isUserTeacher && (
                        <div className="me-2">
                          <CommonSelect
                            className="select"
                            options={sectionOptions}
                            value={
                              sectionOptions.find(
                                option => option.value === filters.section,
                              ) || null
                            }
                            onChange={handleSectionFilterChange}
                            placeholder={
                              sectionOptions.length > 0
                                ? "Select Section"
                                : "No sections available"
                            }
                          />
                        </div>
                      )}
                      {!isUserTeacher && (
                        <div className="me-2">
                          <CommonSelect
                            className="select"
                            options={termOptions}
                            value={
                              termOptions.find(
                                option => option.value === filters.term,
                              ) || null
                            }
                            onChange={selectedOption =>
                              handleFilterChange(
                                "term",
                                selectedOption?.value || "",
                              )
                            }
                            placeholder={
                              termOptions.length > 0
                                ? "Select Term"
                                : "No terms available"
                            }
                            isDisabled={termOptions.length === 0}
                          />
                        </div>
                      )}
                    </div>
                    {!isUserTeacher && (
                      <div className="dropdown mb-3 me-2">
                        <Link
                          to="#"
                          className="btn btn-outline-light bg-white dropdown-toggle"
                          data-bs-toggle="dropdown"
                          data-bs-auto-close="outside"
                        >
                          <i className="ti ti-filter me-2" />
                          Filter
                        </Link>
                        <div
                          className="dropdown-menu drop-width"
                          ref={dropdownMenuRef}
                        >
                          <form>
                            <div className="d-flex align-items-center border-bottom p-3">
                              <h4>Filter</h4>
                            </div>
                            <div className="p-3 border-bottom pb-0">
                              <div className="row">
                                <div className="col-md-4">
                                  <div className="mb-3">
                                    <label className="form-label">Course</label>
                                    <CommonSelect
                                      className="select"
                                      options={filteredSubjectOptions}
                                      value={
                                        filteredSubjectOptions.find(
                                          option =>
                                            option.value === filters.course,
                                        ) || null
                                      }
                                      isSearchable={true}
                                      onChange={selectedOption =>
                                        handleFilterChange(
                                          "course",
                                          selectedOption?.value || "",
                                        )
                                      }
                                    />
                                  </div>
                                </div>
                                <div className="col-md-4">
                                  <div className="mb-3">
                                    <label className="form-label">
                                      Teacher
                                    </label>
                                    <CommonSelect
                                      className="select"
                                      options={teacherOptions}
                                      value={
                                        teacherOptions.find(
                                          option =>
                                            option.value === filters.teacher,
                                        ) || null
                                      }
                                      isSearchable={true}
                                      onChange={selectedOption =>
                                        handleFilterChange(
                                          "teacher",
                                          selectedOption?.value || "",
                                        )
                                      }
                                      placeholder="Select Teacher"
                                    />
                                  </div>
                                </div>
                                <div className="col-md-4">
                                  <div className="mb-3">
                                    <label className="form-label">Day</label>
                                    <CommonSelect
                                      className="select"
                                      options={DAY_OPTIONS}
                                      value={
                                        DAY_OPTIONS.find(
                                          option =>
                                            option.value === filters.dayOfWeek,
                                        ) || null
                                      }
                                      isSearchable={true}
                                      onChange={selectedOption =>
                                        handleFilterChange(
                                          "dayOfWeek",
                                          selectedOption?.value || "",
                                        )
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="p-3 d-flex align-items-center justify-content-end">
                              <Link
                                to="#"
                                className="btn btn-light me-3"
                                onClick={handleResetFilters}
                              >
                                Reset
                              </Link>
                              <Link
                                to="#"
                                className="btn btn-primary"
                                onClick={handleApplyClick}
                              >
                                Apply
                              </Link>
                            </div>
                          </form>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          {!hasInitialDataFetched ? (
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading timetable data...</p>
              </div>
            </div>
          ) : shouldShowTimetable ? (
            <div
              className="card printable-content"
              ref={componentRef}
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                className="card-body pb-0"
                style={{
                  flex: 1,
                  overflowY: "auto",
                }}
              >
                <div className="print-only-header">
                  <div className="school-brand">
                    <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                    <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
                  </div>
                  <div className="report-title">Class Timetable</div>
                  <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
                </div>
                {/* Print Header - only visible when printing */}
                {/* Print Header - only visible when printing */}
                <div className="print-header" style={{ display: "none" }}>
                  <h3>
                    {isUserTeacher
                      ? "My Teaching Timetable"
                      : isUserStudent
                        ? `${selectedClassName} - Class Timetable`
                        : classIdFromUrl || filters.class
                          ? `${selectedClassName} - Class Timetable (Monday - Sunday)`
                          : "School Timetable (Monday - Sunday)"}
                  </h3>
                  <p>
                    Term:{" "}
                    {termOptions.find(t => t.value === filters.term)?.label ||
                      "All Terms"}{" "}
                    | Days: Monday - Sunday (Full Week)
                  </p>
                </div>

                <div className="d-flex flex-nowrap h-100">
                  {Object.entries(groupedData).map(([day, routines]) => (
                    <div
                      key={day}
                      className="d-flex flex-column me-4"
                      style={{
                        flex: "1 0 20%",
                        minWidth: "180px",
                        height: "100%",
                        overflowY: "auto",
                      }}
                    >
                      <div className="mb-3">
                        <h6>{day}</h6>
                      </div>
                      <div style={{ flex: 1 }}>
                        {routines.length > 0 ? (
                          routines.map((routine, index) => (
                            <div
                              key={routine._id}
                              className={`bg-${getColorClass(
                                index,
                              )} rounded p-2 pe-4 mb-2 position-relative`}
                            >
                              {isUserTeacher && routine.breakTime && (
                                <div className="break-time-badge mb-2">
                                  <span className="badge bg-warning">
                                    <i className="ti ti-coffee me-1" />
                                    Break: {routine.breakTime.startTime} -{" "}
                                    {routine.breakTime.endTime}
                                  </span>
                                </div>
                              )}
                              {showActions && (
                                <div className="dropdown action-drop position-absolute top-0 end-0 mt-2 me-2">
                                  <Link
                                    to="#"
                                    className="action-arrow"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                  >
                                    <i className="ti ti-dots-vertical" />
                                  </Link>
                                  <div className="dropdown-menu dropdown-menu-end">
                                    {canEditTimetable && (
                                      <Link
                                        to="#"
                                        className={`dropdown-item ${
                                          user.permissions?.includes(
                                            "class-timetable.edit",
                                          ) || !user.permissions?.length
                                            ? ""
                                            : "not-allowed"
                                        }`}
                                        data-bs-toggle="modal"
                                        data-bs-target="#edit_time_table"
                                        onClick={e => {
                                          if (
                                            !user.permissions?.includes(
                                              "class-timetable.edit",
                                            ) &&
                                            !!user.permissions?.length
                                          ) {
                                            e.preventDefault()
                                            e.stopPropagation()
                                          } else {
                                            handleEditClick(routine)
                                          }
                                        }}
                                      >
                                        <i className="ti ti-edit me-2" />
                                        Edit
                                      </Link>
                                    )}
                                    {canDeleteTimetable && (
                                      <Link
                                        to="#"
                                        className={`dropdown-item text-danger ${
                                          user.permissions?.includes(
                                            "class-timetable.delete",
                                          ) || !user.permissions?.length
                                            ? ""
                                            : "not-allowed"
                                        }`}
                                        data-bs-toggle="modal"
                                        data-bs-target="#delete-modal"
                                        onClick={e => {
                                          if (
                                            !user.permissions?.includes(
                                              "class-timetable.delete",
                                            ) &&
                                            !!user.permissions?.length
                                          ) {
                                            e.preventDefault()
                                            e.stopPropagation()
                                          } else {
                                            setSelectedRecord(routine)
                                          }
                                        }}
                                      >
                                        <i className="ti ti-trash me-2" />
                                        Delete
                                      </Link>
                                    )}
                                  </div>
                                </div>
                              )}
                              <p className="d-flex align-items-center justify-content-between mb-1">
                                <span className="d-flex align-items-center me-2">
                                  <i className="ti ti-clock me-1" />
                                  <span className="small">
                                    {routine.startTime} - {routine.endTime}
                                  </span>
                                </span>
                                <span className="text-dark text-truncate ms-2 small fw-semibold">
                                  {routine.course?.title ||
                                    routine.course?.courseIdentifier ||
                                    routine.course?.name ||
                                    "N/A"}
                                </span>
                              </p>
                              <p className="mb-0 mt-1 text-muted small text-truncate">
                                {isUserTeacher
                                  ? "You"
                                  : routine.teacher?.firstName &&
                                      routine.teacher?.lastName
                                    ? `${routine.teacher.firstName} ${routine.teacher.lastName}`
                                    : routine.teacher?.email || "N/A"}
                              </p>
                            </div>
                          ))
                        ) : (
                          <div className="text-center text-muted p-3">
                            No classes scheduled
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body text-center py-5">
                <i
                  className="ti ti-calendar-off text-muted mb-3"
                  style={{ fontSize: "3rem" }}
                ></i>
                <h4 className="text-muted mb-2">No Timetable Data</h4>
                <p className="text-muted mb-0">
                  No timetable data found for selected filters
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      {!isUserStudent && (
        <>
          {/* Add Time Table Modal */}
          {canCreateTimetable && !isUserTeacher && (
            <div className="modal fade" id="add_time_table">
              <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content">
                  <div className="modal-header">
                    <h4 className="modal-title">Add Time Table</h4>
                    <button
                      type="button"
                      className="btn-close custom-btn-close"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                      onClick={() => {
                        setFormData(getInitialFormData())
                        setActiveDay("Monday")
                      }}
                    >
                      <i className="ti ti-x" />
                    </button>
                  </div>
                  <form onSubmit={handleCreateClassRoutine}>
                    <div className="modal-body">
                      <div className="row">
                        <div className="col-lg-4">
                          <div className="mb-3">
                            <label className="form-label">Class</label>
                            <CommonSelect
                              className="select"
                              options={classOptions}
                              value={
                                classOptions.find(
                                  option => option.value === formData.class,
                                ) || null
                              }
                              isSearchable={true}
                              onChange={selectedOption =>
                                handleInputChange(
                                  "class",
                                  selectedOption?.value || "",
                                )
                              }
                              required
                            />
                          </div>
                        </div>
                        <div className="col-lg-4">
                          <div className="mb-3">
                            <label className="form-label">Section</label>
                            <CommonSelect
                              className="select"
                              options={sectionOptions}
                              value={
                                sectionOptions.find(
                                  option => option.value === formData.section,
                                ) || null
                              }
                              isSearchable={true}
                              onChange={selectedOption =>
                                handleInputChange(
                                  "section",
                                  selectedOption?.value || "",
                                )
                              }
                              required
                            />
                          </div>
                        </div>
                        <div className="col-lg-4">
                          <div className="mb-3">
                            <label className="form-label">Term</label>
                            <CommonSelect
                              className="select"
                              options={termOptions}
                              value={
                                termOptions.find(
                                  option => option.value === formData.term,
                                ) || null
                              }
                              isSearchable={true}
                              onChange={selectedOption =>
                                handleInputChange(
                                  "term",
                                  selectedOption?.value || "",
                                )
                              }
                              required
                              isDisabled={termOptions.length === 0}
                            />
                            {termOptions.length === 0 && (
                              <div className="text-danger small mt-1">
                                No terms available for this class in current
                                session
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="add-more-timetable mt-3">
                        <ul
                          className="tab-links nav nav-pills"
                          id="pills-tab2"
                          role="tablist"
                        >
                          {Object.keys(dayRows).map(day => (
                            <li key={day} className="nav-item">
                              <button
                                className={`nav-link ${
                                  activeDay === day ? "active" : ""
                                }`}
                                type="button"
                                onClick={() => {
                                  setActiveDay(day)
                                  handleInputChange("dayOfWeek", day)
                                }}
                              >
                                {day}
                              </button>
                            </li>
                          ))}
                        </ul>
                        <div className="tab-content pt-0 dashboard-tab">
                          {Object.keys(dayRows).map(day => (
                            <div
                              key={day}
                              className={`tab-pane fade ${
                                activeDay === day ? "show active" : ""
                              }`}
                              id={`pills-${day.toLowerCase()}`}
                            >
                              {dayRows[day].map(row => (
                                <div
                                  key={row.id}
                                  className="add-timetable-row mb-4 p-3 border rounded position-relative"
                                >
                                  {dayRows[day].length > 1 && (
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm position-absolute top-0 end-0 mt-5 me-0 d-flex align-items-center text-center"
                                      onClick={() => removeDayRow(day, row.id)}
                                      style={{
                                        height: "32px",
                                      }}
                                      title="Delete"
                                    >
                                      <i className="ti ti-trash" />
                                    </button>
                                  )}
                                  <div className="row timetable-count">
                                    <div className="col-lg-3">
                                      <div className="mb-3">
                                        <label className="form-label">
                                          Course *
                                        </label>
                                        <CommonSelect
                                          className="select"
                                          options={
                                            filteredSubjectOptions.length > 0
                                              ? filteredSubjectOptions
                                              : []
                                          }
                                          value={
                                            filteredSubjectOptions.find(
                                              option =>
                                                option.value === row.course,
                                            ) || null
                                          }
                                          isSearchable={true}
                                          onChange={selectedOption =>
                                            handleCourseSelection(
                                              day,
                                              row.id,
                                              selectedOption?.value || "",
                                            )
                                          }
                                          required
                                        />
                                      </div>
                                    </div>
                                    <div className="col-lg-3">
                                      <div className="mb-3">
                                        <label className="form-label">
                                          Teacher
                                        </label>
                                        <CommonSelect
                                          className="select"
                                          options={teacherOptions}
                                          value={
                                            teacherOptions.find(
                                              teacher =>
                                                teacher.value === row.teacher,
                                            ) || null
                                          }
                                          isSearchable={true}
                                          onChange={selectedOption => {
                                            const teacherId =
                                              selectedOption?.value || ""
                                            const teacherName =
                                              selectedOption?.label || ""
                                            updateDayRow(
                                              day,
                                              row.id,
                                              "teacher",
                                              teacherId,
                                            )
                                            updateDayRow(
                                              day,
                                              row.id,
                                              "teacherName",
                                              teacherName,
                                            )
                                          }}
                                          placeholder="Select Teacher"
                                        />
                                      </div>
                                    </div>
                                    <div className="col-lg-2">
                                      <div className="mb-3">
                                        <label className="form-label">
                                          Start Time *
                                        </label>
                                        <div className="date-pic">
                                          <TimePicker
                                            getPopupContainer={
                                              getModalContainer
                                            }
                                            use12Hours
                                            placeholder="Choose"
                                            format="h:mm A"
                                            className="form-control timepicker"
                                            value={formatTimeForDisplay(
                                              row.startTime,
                                            )}
                                            onChange={time =>
                                              handleTimeChange(
                                                day,
                                                row.id,
                                                "startTime",
                                                time,
                                              )
                                            }
                                          />
                                          <span className="cal-icon">
                                            <i className="ti ti-clock" />
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-lg-2">
                                      <div className="mb-3">
                                        <label className="form-label">
                                          End Time *
                                        </label>
                                        <div className="date-pic">
                                          <TimePicker
                                            getPopupContainer={
                                              getModalContainer
                                            }
                                            use12Hours
                                            placeholder="Choose"
                                            format="h:mm A"
                                            className="form-control timepicker"
                                            value={formatTimeForDisplay(
                                              row.endTime,
                                            )}
                                            onChange={time =>
                                              handleTimeChange(
                                                day,
                                                row.id,
                                                "endTime",
                                                time,
                                              )
                                            }
                                          />
                                          <span className="cal-icon">
                                            <i className="ti ti-clock" />
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                              <div className="mt-3">
                                <button
                                  type="button"
                                  className="btn btn-primary add-new-timetable"
                                  onClick={() => addDayRow(day)}
                                >
                                  <i className="ti ti-square-rounded-plus-filled me-2" />
                                  Add New Period
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-light me-2"
                        data-bs-dismiss="modal"
                        onClick={() => {
                          setFormData(getInitialFormData())
                          setActiveDay("Monday")
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSubmitting || termOptions.length === 0}
                      >
                        {isSubmitting ? "Creating..." : "Add Time Table"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
          {/* Edit Time Table Modal */}
          {canEditTimetable && !isUserTeacher && (
            <div className="modal fade" id="edit_time_table">
              <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content">
                  <div className="modal-header">
                    <h4 className="modal-title">Edit Time Table</h4>
                    <button
                      type="button"
                      className="btn-close custom-btn-close"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    >
                      <i className="ti ti-x" />
                    </button>
                  </div>
                  <form onSubmit={handleUpdateClassRoutine}>
                    <div className="modal-body">
                      <div className="row">
                        <div className="col-lg-3">
                          <div className="mb-3">
                            <label className="form-label">Class</label>
                            <CommonSelect
                              className="select"
                              options={classOptions}
                              value={
                                classOptions.find(
                                  option => option.value === editFormData.class,
                                ) || null
                              }
                              isSearchable={true}
                              onChange={selectedOption =>
                                handleEditInputChange(
                                  "class",
                                  selectedOption?.value || "",
                                )
                              }
                            />
                          </div>
                        </div>
                        <div className="col-lg-3">
                          <div className="mb-3">
                            <label className="form-label">Section</label>
                            <CommonSelect
                              className="select"
                              options={sectionOptions}
                              value={
                                sectionOptions.find(
                                  option =>
                                    option.value === editFormData.section,
                                ) || null
                              }
                              isSearchable={true}
                              onChange={selectedOption =>
                                handleEditInputChange(
                                  "section",
                                  selectedOption?.value || "",
                                )
                              }
                            />
                          </div>
                        </div>
                        <div className="col-lg-3">
                          <div className="mb-3">
                            <label className="form-label">Course</label>
                            <CommonSelect
                              className="select"
                              options={
                                filteredSubjectOptions.length > 0
                                  ? filteredSubjectOptions
                                  : []
                              }
                              value={
                                filteredSubjectOptions.find(
                                  option =>
                                    option.value === editFormData.course,
                                ) || null
                              }
                              isSearchable={true}
                              onChange={selectedOption =>
                                handleEditInputChange(
                                  "course",
                                  selectedOption?.value || "",
                                )
                              }
                            />
                          </div>
                        </div>
                        <div className="col-lg-3">
                          <div className="mb-3">
                            <label className="form-label">Term</label>
                            <CommonSelect
                              className="select"
                              options={termOptions}
                              value={
                                termOptions.find(
                                  option => option.value === editFormData.term,
                                ) || null
                              }
                              isSearchable={true}
                              onChange={selectedOption =>
                                handleEditInputChange(
                                  "term",
                                  selectedOption?.value || "",
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                      <div className="add-more-timetable">
                        <div className="add-timetable-row">
                          <div className="row timetable-count">
                            <div className="col-lg-3">
                              <div className="mb-3">
                                <label className="form-label">Day</label>
                                <CommonSelect
                                  className="select"
                                  options={DAY_OPTIONS}
                                  value={
                                    DAY_OPTIONS.find(
                                      option =>
                                        option.value === editFormData.dayOfWeek,
                                    ) || null
                                  }
                                  isSearchable={true}
                                  onChange={selectedOption =>
                                    handleEditInputChange(
                                      "dayOfWeek",
                                      selectedOption?.value || "",
                                    )
                                  }
                                />
                              </div>
                            </div>
                            <div className="col-lg-3">
                              <div className="mb-3">
                                <label className="form-label">Teacher</label>
                                <CommonSelect
                                  className="select"
                                  options={teacherOptions}
                                  value={
                                    teacherOptions.find(
                                      teacher =>
                                        teacher.value === editFormData.teacher,
                                    ) || null
                                  }
                                  isSearchable={true}
                                  onChange={selectedOption => {
                                    const teacherId =
                                      selectedOption?.value || ""
                                    const teacherName =
                                      selectedOption?.label || ""
                                    handleEditInputChange("teacher", teacherId)
                                    setEditFormData(prev => ({
                                      ...prev,
                                      teacherName: teacherName,
                                    }))
                                  }}
                                  placeholder="Select Teacher"
                                />
                              </div>
                            </div>
                            <div className="col-lg-3">
                              <div className="mb-3">
                                <label className="form-label">Start Time</label>
                                <div className="date-pic">
                                  <TimePicker
                                    getPopupContainer={getModalContainer}
                                    use12Hours
                                    placeholder="Choose"
                                    format="h:mm A"
                                    className="form-control timepicker"
                                    value={formatTimeForDisplay(
                                      editFormData.startTime || "",
                                    )}
                                    onChange={time =>
                                      handleEditTimeChange("startTime", time)
                                    }
                                  />
                                  <span className="cal-icon">
                                    <i className="ti ti-clock" />
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="col-lg-3">
                              <div className="mb-3">
                                <label className="form-label">End Time</label>
                                <div className="date-pic">
                                  <TimePicker
                                    getPopupContainer={getModalContainer}
                                    use12Hours
                                    placeholder="Choose"
                                    format="h:mm A"
                                    className="form-control timepicker"
                                    value={formatTimeForDisplay(
                                      editFormData.endTime || "",
                                    )}
                                    onChange={time =>
                                      handleEditTimeChange("endTime", time)
                                    }
                                  />
                                  <span className="cal-icon">
                                    <i className="ti ti-clock" />
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-light me-2"
                        data-bs-dismiss="modal"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Updating..." : "Save Changes"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
          {/* Delete Modal */}
          {canDeleteTimetable && !isUserTeacher && (
            <div className="modal fade" id="delete-modal">
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <form>
                    <div className="modal-body text-center">
                      <span className="delete-icon">
                        <i className="ti ti-trash-x" />
                      </span>
                      <h4>Confirm Deletion</h4>
                      <p>
                        Are you sure you want to delete this time table? This
                        action cannot be undone.
                      </p>
                      <div className="d-flex justify-content-center">
                        <button
                          type="button"
                          className="btn btn-light me-3"
                          data-bs-dismiss="modal"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={handleDeleteClassRoutine}
                        >
                          Yes, Delete
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ClassTimetable
